/**
 * ==========================================
 * ANIMATION.JS - GSAP Animation System
 * ==========================================
 *
 * Diese Datei enthält alle GSAP-basierten Animationen für die Website.
 * Alle Animationen wurden für optimale Performance konsolidiert.
 *
 * VERWENDETE TECHNOLOGIEN:
 * - GSAP (GreenSock Animation Platform)
 * - GSAP ScrollTrigger Plugin
 * - IntersectionObserver API
 *
 * STRUKTUR:
 * 1. Intro Animationen (Mobile & Desktop)
 * 2. Stage Animationen
 * 3. ScrollTrigger-basierte Projekt-Animationen
 * 4. IntersectionObserver für Inview & CountUp
 * 5. Magnetic Button Effekt
 * 6. Scroll-basierte Animationen (6 Sektionen)
 *
 * PERFORMANCE OPTIMIERUNGEN:
 * - gsap.quickTo() für hochfrequente Updates (Magnetic Buttons)
 * - willChange Properties werden nach Animation entfernt
 * - ScrollTrigger mit scrub für flüssige Scroll-Animationen
 * - IntersectionObserver für lazy-loading von Animationen
 *
 * @author Marcello Insolera
 * @version 2.0.0 - Migriert von anime.js + ScrollCaptain zu reinem GSAP
 */

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

// ==========================================
// 1. INTRO ANIMATIONEN
// ==========================================

const $intro = document.querySelector(".intro");
const $headline = document.querySelector(".intro__headline");

/**
 * Intro Animation mit responsiven Timelines
 * - Mobile (≤767px): Einfachere Animation mit scale transitions
 * - Desktop (≥768px): Erweiterte Animation mit y-Positionen
 * - Feuert "introEnd" Event nach Completion
 */
function introAnimation() {
  const allLetters = $headline.querySelectorAll("[data-char]");
  // Mobile animation
  gsap.matchMedia().add("(max-width: 767px)", () => {
    const timeline = gsap.timeline({
      defaults: { duration: 0.6, ease: "power1.out" },
      onComplete: function () {
        const event = new CustomEvent("introEnd");
        document.dispatchEvent(event);
      },
    });

    timeline
      .fromTo(
        ".intro__image-frame",
        { willChange: "filter, transform", filter: "blur(20px)" },
        {
          filter: "blur(0px)",
          duration: 0.8,
          ease: "sine",
          onComplete: () => gsap.set(".intro__image-frame", { clearProps: "willChange" }),
        }
      )
      .fromTo(
        allLetters,
        { scale: 0.9, willChange: "filter, transform", filter: "blur(8px)", opacity: 0, transformOrigin: "center center" },
        {
          scale: 1,
          filter: "blur(0px)",
          opacity: 1,
          color: "#fff",
          stagger: 0.05,
          ease: "sine",
          onComplete: () => gsap.set(allLetters, { clearProps: "willChange" }),
        }
      )
      .fromTo(".header__nav-frame", { scale: 0 }, { scale: 1, duration: 0.3 }, "-=1.2")
      .fromTo(".header__logo-icon", { scale: 0 }, { scale: 1, duration: 0.4 })
      .fromTo(".intro__lead-in", { opacity: 0 }, { opacity: 1, duration: 0.4 })
      .fromTo(".intro__hint", { scale: 0 }, { scale: 1, ease: "sine" })
      .fromTo(
        ".intro__hint span",
        { willChange: "filter", filter: "blur(10px)", opacity: 0 },
        {
          opacity: 1,
          filter: "blur(0px)",
          ease: "none",
          duration: 0.6,
          onComplete: () => gsap.set(".intro__hint span", { clearProps: "willChange" }),
        },
        "-=0.4"
      );
  });

  // Desktop animation
  gsap.matchMedia().add("(min-width: 768px)", () => {
    const timeline = gsap.timeline({
      defaults: { duration: 0.6, ease: "power1.out" },
      onComplete: function () {
        const event = new CustomEvent("introEnd");
        document.dispatchEvent(event);
      },
    });

    timeline
      .fromTo(
        ".intro__image-frame",
        { willChange: "filter, transform", filter: "blur(20px)" },
        {
          filter: "blur(0px)",
          duration: 0.8,
          ease: "sine",
          onComplete: () => gsap.set(".intro__image-frame", { clearProps: "willChange" }),
        }
      )
      .fromTo(
        allLetters,
        { scale: 0.9, willChange: "filter, transform", filter: "blur(8px)", color: "#303336", transformOrigin: "center center" },
        {
          scale: 1,
          filter: "blur(0px)",
          color: "#fff",
          stagger: 0.05,
          ease: "sine",
          onComplete: () => gsap.set(allLetters, { clearProps: "willChange" }),
        }
      )
      .fromTo(".header__nav-frame", { y: -100 }, { y: 0, duration: 0.4 }, "-=1.2")
      .fromTo(".header__logo-icon", { scale: 0 }, { scale: 1, duration: 0.4 })
      .fromTo(
        ".intro__lead-in",
        { willChange: "filter, transform", filter: "blur(20px)", opacity: 0 },
        {
          filter: "blur(0px)",
          opacity: 1,
          duration: 0.4,
          onComplete: () => gsap.set(".intro__lead-in", { clearProps: "willChange" }),
        }
      )
      .fromTo(".intro__hint", { scale: 0 }, { scale: 1, ease: "sine" });
  });
}

// ==========================================
// 2. STAGE ANIMATIONEN
// ==========================================

/**
 * Stage Animation für Projekt-Seiten
 * - Animiert Overlay, Letters, und Hint-Elemente
 * - Verwendet stagger für sequenzielle Letter-Animation
 * @param {HTMLElement} el - Stage Container Element
 */
const stageAnimation = (el) => {
  const allLetters = el.querySelectorAll("[data-char]");

  const timeline = gsap.timeline({
    defaults: { duration: 0.75, ease: "power2.out" },
  });

  timeline.fromTo(".stage__overlay", { scale: 2 }, { scale: 1, duration: 0.8 }).fromTo(allLetters, { y: "-150%", scale: 0 }, { y: "0%", scale: 1, duration: 0.4, stagger: 0.05 }).fromTo(".stage__hint", { scale: 0 }, { scale: 1 }, "-=0.25").fromTo(".stage__hint span", { scale: 0 }, { scale: 1 }, "-=0.25");
};

// ==========================================
// 3. SCROLLTRIGGER PROJEKT-ANIMATIONEN
// ==========================================

const $stage = document.querySelector(".stage");

/**
 * DOMContentLoaded Handler
 * Initialisiert Intro/Stage Animationen und ScrollTrigger für Projekte
 */
document.addEventListener("DOMContentLoaded", () => {
  document.body.classList.add("show");
  if ($intro) {
    introAnimation();

    gsap.fromTo(
      '[data-project-intro="headline"]',
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        scrollTrigger: {
          trigger: '[data-project-intro="root"]',
          start: "top 80%",
          toggleActions: "play none none none",
        },
      }
    );

    gsap.fromTo(
      '[data-project-intro="copy"]',
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        scrollTrigger: {
          trigger: '[data-project-intro="headline"]',
          start: "top 80%",
          toggleActions: "play none none none",
          once: true,
        },
      }
    );
  }

  if ($stage) {
    stageAnimation($stage);
  }
});

// ==========================================
// 4. INTERSECTION OBSERVER
// ==========================================

/**
 * IntersectionObserver für Inview-Animationen
 * Fügt/entfernt 'inview' Klasse basierend auf Viewport-Sichtbarkeit
 */
const ioFadeOptions = {
  threshold: 0.5,
  rootMargin: "0px 0px -10px 0px",
};

const ioFadeIn = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) {
      entry.target.classList.remove("inview");
    } else {
      entry.target.classList.add("inview");
    }
  });
}, ioFadeOptions);

// Beobachte Elemente mit data-inview
const $$inview = document.querySelectorAll("[data-inview]");
$$inview.forEach((inview) => {
  ioFadeIn.observe(inview);
});

/**
 * CountUp Animation mit GSAP
 * Animiert Zahlen von 0 zum Zielwert mit IntersectionObserver
 * @exports createCountUpObserver
 */
export const createCountUpObserver = () => {
  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const target = entry.target;
        const endValue = parseInt(target.getAttribute("data-count"), 10);

        // GSAP countUp Animation
        gsap.to(target, {
          textContent: endValue,
          duration: 2,
          ease: "power1.out",
          snap: { textContent: 1 },
          onUpdate: function () {
            target.textContent = Math.floor(this.targets()[0].textContent);
          },
        });

        observer.unobserve(target);
      }
    });
  });

  document.querySelectorAll("[data-count]").forEach((element) => {
    observer.observe(element);
  });
};

// ==========================================
// 5. MAGNETIC BUTTON EFFEKT
// ==========================================

/**
 * Magnetic Button Effekt mit GSAP quickTo()
 * - Verwendet quickTo() für optimale Performance bei mousemove
 * - Button folgt dem Cursor im magnetischen Feld
 * - Kehrt zu Ausgangsposition bei mouseleave zurück
 */
const magnets = document.querySelectorAll(".magnatic-cta");
const strength = 100;

magnets.forEach((magnet) => {
  // quickTo für Performance: kein neues Tween bei jedem mousemove
  const xTo = gsap.quickTo(magnet, "x", { duration: 1, ease: "power2.out" });
  const yTo = gsap.quickTo(magnet, "y", { duration: 1, ease: "power2.out" });

  magnet.addEventListener("mousemove", (event) => {
    const bounding = magnet.getBoundingClientRect();
    const x = ((event.clientX - bounding.left) / magnet.offsetWidth - 0.5) * strength;
    const y = ((event.clientY - bounding.top) / magnet.offsetHeight - 0.5) * strength;

    xTo(x);
    yTo(y);
  });

  magnet.addEventListener("mouseleave", () => {
    xTo(0);
    yTo(0);
  });
});

// ==========================================
// 6. SCROLL-BASIERTE ANIMATIONEN
// ==========================================
// Alle ScrollCaptain-Animationen wurden zu GSAP ScrollTrigger migriert
// für bessere Performance und einheitliche API

/**
 * Projects Section Animation
 * - Animiert Image Wrapper von rechts nach links
 * - Setzt CSS Custom Property --pos für zusätzliche Effekte
 * @exports initProjectsAnimation
 */
export const initProjectsAnimation = () => {
  const $scrollCaptainProjects = document.querySelector('[data-scrollcaptain="projects"]');
  if (!$scrollCaptainProjects) return;

  const $imageWrapper = document.querySelector('[data-scrollcaptain="image-wrapper"]');

  gsap.to($imageWrapper, {
    x: 0,
    scrollTrigger: {
      trigger: $scrollCaptainProjects,
      start: "top bottom",
      end: "top 60%",
      scrub: true,
      onUpdate: (self) => {
        $scrollCaptainProjects.style.setProperty("--pos", Math.round(self.progress * 100) + "%");
      },
    },
  });

  gsap.fromTo(
    $imageWrapper,
    { x: "100%" },
    {
      x: 0,
      scrollTrigger: {
        trigger: $scrollCaptainProjects,
        start: "top bottom",
        end: "top 60%",
        scrub: true,
      },
    }
  );
};

/**
 * About Intro Animation
 * - Parallax-Effekt für Text und Image
 * - Responsiv mit verschiedenen Scroll-Geschwindigkeiten
 * @exports initAboutIntroAnimation
 */
export const initAboutIntroAnimation = () => {
  const $scAboutIntro = document.querySelector('[data-scroll-about="root"]');
  const $scAboutImage = document.querySelector('[data-scroll-about="about-image"]');
  const $scAboutIntroText = document.querySelector('[data-scroll-about="about-intro-text"]');

  if (!$scAboutIntro) return;

  gsap.to($scAboutIntroText, {
    y: "100%",
    scrollTrigger: {
      trigger: $scAboutIntro,
      start: "top bottom",
      end: "bottom top",
      scrub: true,
    },
  });

  gsap.to($scAboutImage, {
    scrollTrigger: {
      trigger: $scAboutIntro,
      start: "top bottom",
      end: "bottom 50%",
      scrub: true,
      onUpdate: (self) => {
        $scAboutImage.style.setProperty("--pos", Math.round(self.progress * 100) * 2 + "%");
      },
    },
  });
};

/**
 * Projects Intro Animation
 * - Horizontale Bewegung mit Breakpoints
 * - Mobile: 100%, Desktop: 200% Translation
 * @exports initProjectsIntroAnimation
 */
export const initProjectsIntroAnimation = () => {
  const $projectsIntro = document.querySelector(".projects__intro");
  if (!$projectsIntro) return;

  const $contact = document.querySelector('[data-project-intro="contact"]');
  if (!$contact) return;

  const mm = gsap.matchMedia();

  mm.add("(max-width: 1023px)", () => {
    gsap.fromTo(
      $contact,
      { x: 0, force3D: true },
      {
        x: "100%",
        force3D: true,
        ease: "none",
        scrollTrigger: {
          trigger: $projectsIntro,
          start: "top 80%",
          end: "bottom top",
          scrub: 1,
          invalidateOnRefresh: true,
        },
      }
    );
  });

  mm.add("(min-width: 1024px)", () => {
    gsap.fromTo(
      $contact,
      { x: 0, force3D: true },
      {
        x: "200%",
        force3D: true,
        ease: "none",
        scrollTrigger: {
          trigger: $projectsIntro,
          start: "top 80%",
          end: "bottom top",
          scrub: 1,
          invalidateOnRefresh: true,
        },
      }
    );
  });
};

/**
 * Intro Contact Animation
 * - Wird nach "introEnd" Event initialisiert
 * - Mobile: Scale + X Translation, Desktop: Scale + Y Translation
 * @exports initIntroContactAnimation
 */
export const initIntroContactAnimation = () => {
  const $intro = document.querySelector('[data-intro="root"]');
  if (!$intro) return;

  const $contact = document.querySelector('[data-intro="contact"]');
  if (!$contact) return;

  const mm = gsap.matchMedia();

  mm.add("(max-width: 1023px)", () => {
    gsap.fromTo(
      $contact,
      { scale: 1, x: 0, force3D: true },
      {
        scale: 1.4,
        x: "-100%",
        force3D: true,
        ease: "none",
        scrollTrigger: {
          trigger: $intro,
          start: "top top",
          end: "bottom top",
          scrub: 1,
          invalidateOnRefresh: true,
        },
      }
    );
  });

  mm.add("(min-width: 1024px)", () => {
    gsap.fromTo(
      $contact,
      { scale: 1, x: 0, y: 0, force3D: true },
      {
        scale: 1.1,
        y: "200%",
        force3D: true,
        ease: "none",
        scrollTrigger: {
          trigger: $intro,
          start: "top top",
          end: "bottom top",
          scrub: 1,
          invalidateOnRefresh: true,
        },
      }
    );
  });
};

/**
 * About Ghost Animation
 * - Parallax-Effekt für Ghost-Elemente
 * - Berechnete Bewegung basierend auf Container-Breite
 * @exports initAboutGhostAnimation
 */
export const initAboutGhostAnimation = () => {
  const $about = document.querySelector('[data-about="root"]');
  if (!$about) return;

  const $ghost = document.querySelector('[data-about="ghost"]');
  const $ghostFirst = document.querySelector('[data-about="ghost-first"]');

  const w = $about.getBoundingClientRect().width;
  const d = w * 3;
  const move = d * -1;

  gsap.fromTo(
    $ghost,
    { x: 0 },
    {
      x: (move / 2) * -1,
      scrollTrigger: {
        trigger: $about,
        start: "top 75%",
        end: "bottom top",
        scrub: true,
      },
    }
  );

  gsap.fromTo(
    $ghostFirst,
    { x: 0 },
    {
      x: move / 2,
      scrollTrigger: {
        trigger: $about,
        start: "top 75%",
        end: "bottom top",
        scrub: true,
        onUpdate: (self) => {
          $about.style.setProperty("--pos", Math.round(self.progress * 100) + "%");
        },
      },
    }
  );
};

/**
 * Process Section Animation
 * - Label + Headline fade up on enter
 * - Vertical line draws down with scrub
 * - Each step: marker scales in, content + visual slide from outside
 * - Responsive: horizontal slide on desktop, fade up on mobile
 * @exports initProcessAnimation
 */
export const initProcessAnimation = () => {
  const $process = document.querySelector('[data-process="root"]');
  if (!$process) return;

  // Label
  gsap.fromTo(
    '[data-process="label"]',
    { opacity: 0, y: 16 },
    {
      opacity: 1,
      y: 0,
      duration: 0.5,
      scrollTrigger: {
        trigger: $process,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    }
  );

  // Headline
  gsap.fromTo(
    '[data-process="headline"]',
    { opacity: 0, y: 24 },
    {
      opacity: 1,
      y: 0,
      duration: 0.7,
      scrollTrigger: {
        trigger: $process,
        start: "top 75%",
        toggleActions: "play none none none",
      },
    }
  );

  // Line draw
  gsap.fromTo(
    ".process__line",
    { scaleY: 0 },
    {
      scaleY: 1,
      ease: "none",
      scrollTrigger: {
        trigger: ".process__timeline",
        start: "top 70%",
        end: "bottom 85%",
        scrub: 1,
      },
    }
  );

  // Steps
  const mm = gsap.matchMedia();
  const steps = $process.querySelectorAll('[data-process="step"]');

  steps.forEach((step) => {
    const isEven = step.classList.contains("process__step--even");
    const marker = step.querySelector('[data-process="marker"]');
    const content = step.querySelector('[data-process="content"]');
    const visual = step.querySelector('[data-process="visual"]');

    // Marker: always scale in
    gsap.fromTo(
      marker,
      { scale: 0 },
      {
        scale: 1,
        duration: 0.4,
        ease: "back.out(1.7)",
        scrollTrigger: {
          trigger: step,
          start: "top 78%",
          toggleActions: "play none none none",
        },
      }
    );

    // Mobile: fade up
    mm.add("(max-width: 1023px)", () => {
      [content, visual].forEach((el, i) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            delay: i * 0.1,
            scrollTrigger: {
              trigger: step,
              start: "top 80%",
              toggleActions: "play none none none",
            },
          }
        );
      });
    });

    // Desktop: slide from outside
    mm.add("(min-width: 1024px)", () => {
      gsap.fromTo(
        content,
        { opacity: 0, x: isEven ? 50 : -50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.7,
          ease: "power2.out",
          scrollTrigger: {
            trigger: step,
            start: "top 72%",
            toggleActions: "play none none none",
          },
        }
      );

      gsap.fromTo(
        visual,
        { opacity: 0, x: isEven ? -50 : 50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.7,
          ease: "power2.out",
          scrollTrigger: {
            trigger: step,
            start: "top 72%",
            toggleActions: "play none none none",
          },
        }
      );
    });
  });
};

/**
 * Contact Section Animation
 * - Animiert Headline, Links, und Hearth-Element
 * - Responsive mit verschiedenen Verhaltensweisen
 * - Mobile: Scale Animation, Desktop: X-Translation + Scale
 * @exports initContactAnimation
 */
export const initContactAnimation = () => {
  const $contact = document.querySelector('[data-contact="root"]');
  if (!$contact) return;

  const $headline = document.querySelector('[data-contact="headline"]');
  const $links = document.querySelector('[data-contact="links"]');
  const $hearth = document.querySelector('[data-contact="hearth"]');

  gsap.fromTo(
    $headline,
    { y: "-100%" },
    {
      y: 0,
      scrollTrigger: {
        trigger: $contact,
        start: "top bottom",
        end: "top 20%",
        scrub: true,
      },
    }
  );

  gsap.fromTo(
    $links,
    { y: "100%" },
    {
      y: 0,
      scrollTrigger: {
        trigger: $contact,
        start: "top bottom",
        end: "top 20%",
        scrub: true,
      },
    }
  );

  const mm = gsap.matchMedia();

  mm.add("(max-width: 767px)", () => {
    gsap.fromTo(
      $hearth,
      { scale: 0 },
      {
        scale: 1,
        scrollTrigger: {
          trigger: $contact,
          start: "top bottom",
          end: "top 40%",
          scrub: true,
        },
      }
    );
  });

  mm.add("(min-width: 768px)", () => {
    gsap.fromTo(
      $hearth,
      { x: "-100%", scale: 1 },
      {
        x: 0,
        scrollTrigger: {
          trigger: $contact,
          start: "top bottom",
          end: "top 10%",
          scrub: true,
        },
      }
    );
  });
};
